import { useTranslator } from '@bluebeam/react-i18n';
import { Button } from 'components/lib';
import Tooltip from 'components/Tooltip';
import TooltipButton from 'components/Tooltip/TooltipButton';
import ZoomInOut from 'components/ZoomInOut/ZoomInOut';
import { environmentVars } from 'constants/services';
import AppStoreContext from 'contexts/AppStoreContext';
import React, { FC, useContext, useEffect, useState } from 'react';
import ReactMapGL, {
  Layer,
  LinearInterpolator,
  WebMercatorViewport,
} from 'react-map-gl';
import {
  EditingMode,
  Editor as ReactMapGLDraw,
  RENDER_STATE,
} from 'react-map-gl-draw';
import { SheetVersion } from 'records/SheetVersion';
import { calculateCornerCoordinates } from 'utils/sheetCalibration';
import BackButton from '../BackButton';
import {
  CalibrationMachineEventsEnum,
  useCalibrationMachine,
} from '../CalibrationMachine';
import {
  defaultFlyAnimationDuration,
  maxZoomLevel,
  minZoomLevel,
} from '../constants';
import SheetImageSource from '../SheetImageSource';
import CustomDrawPolygonMode from './CustomDrawPolygonMode';
import { useKeyPress } from './KeyPressHook';
import './SheetCropperStyles.css';

type Polygon = {
  type: 'Polygon';
  coordinates: CoordinateArray[][];
};

type Feature = {
  type: 'Feature';
  geometry: Polygon;
  properties?: {
    [key: string]: any;
  };
  id?: string | number;
  bbox?: number[];
};

type ReactMapGLFeatures = {
  data: Feature[];
  editType: string;
  editContext: any;
};

const defaultViewport: Viewport = {
  latitude: 0,
  longitude: 0,
  zoom: 12,
  pitch: 0,
  bearing: 0,
};

const polygonToCropPath = (
  features: Feature[],
  sheetVersion: SheetVersion
): Point[] => {
  if (features.length !== 1) {
    return [];
  }

  const imageCorners = calculateCornerCoordinates({
    center: {
      latitude: 0,
      longitude: 0,
    },
    pdfWidth: sheetVersion.width,
    pdfHeight: sheetVersion.height,
    realFeetPerPaperInch: 200,
  });

  const viewportProjector = new WebMercatorViewport({
    ...defaultViewport,
    height: window.innerHeight,
    width: window.innerWidth,
  });

  const { topleft, bottomright } = imageCorners;
  const [imgTopLeftX, imgTopLeftY] = viewportProjector.project([
    topleft.longitude,
    topleft.latitude,
  ]);
  const [imgBottomRightX, imgBottomRightY] = viewportProjector.project([
    bottomright.longitude,
    bottomright.latitude,
  ]);
  const pdfHeightInPixels = imgBottomRightY - imgTopLeftY;
  const pdfWidthInPixels = imgBottomRightX - imgTopLeftX;

  const polygonFeature = features[0];

  const coordinates = polygonFeature.geometry.coordinates[0];

  const result: Point[] = coordinates.map((coordinate) => {
    const [vertexX, vertexY] = viewportProjector.project(coordinate);

    let x = (vertexX - imgTopLeftX) / pdfWidthInPixels;
    let y = (vertexY - imgTopLeftY) / pdfHeightInPixels;

    if (x < 0) {
      x = 0;
    }

    if (x > 1) {
      x = 1;
    }

    if (y < 0) {
      y = 0;
    }

    if (y > 1) {
      y = 1;
    }

    return { x, y };
  });

  return result;
};

const convertCropPathToGeojson = (
  croppath: Point[],
  sheetVersion: SheetVersion
): Feature[] => {
  if (croppath.length === 0) {
    return [];
  }

  const imageCorners = calculateCornerCoordinates({
    center: {
      latitude: 0,
      longitude: 0,
    },
    pdfWidth: sheetVersion.width,
    pdfHeight: sheetVersion.height,
    realFeetPerPaperInch: 200,
  });

  const viewportProjector = new WebMercatorViewport({
    ...defaultViewport,
    height: window.innerHeight,
    width: window.innerWidth,
  });

  const [imgTopLeftX, imgTopLeftY] = viewportProjector.project([
    imageCorners.topleft.longitude,
    imageCorners.topleft.latitude,
  ]);

  const [imgBottomRightX, imgBottomRightY] = viewportProjector.project([
    imageCorners.bottomright.longitude,
    imageCorners.bottomright.latitude,
  ]);

  const height = imgBottomRightY - imgTopLeftY;
  const width = imgBottomRightX - imgTopLeftX;

  const coordinates: CoordinateArray[] = croppath.map((coordinate) => {
    const x = coordinate.x * width + imgTopLeftX;
    const y = coordinate.y * height + imgTopLeftY;

    return viewportProjector.unproject([x, y]) as CoordinateArray;
  });

  const feature: Feature = {
    geometry: {
      coordinates: [coordinates],
      type: 'Polygon',
    },
    properties: {
      renderType: 'Polygon',
    },
    type: 'Feature',
  };

  return [feature];
};

const SheetCropper: FC = () => {
  const enterPressed = useKeyPress('Enter');

  const backSpacePressed = useKeyPress('Backspace');

  const { actions } = useContext(AppStoreContext);

  const { calibrationMachineState, calibrationMachineService } =
    useCalibrationMachine();
  const {
    sheetVersion,
    calibrationConfig: { croppath },
  } = calibrationMachineState.context;

  const { translate } = useTranslator(['mapView', 'buttons']);

  const [viewport, setViewport] = useState<Viewport>(defaultViewport);

  const [featuresData, setFeaturesData] = useState<Feature[]>(
    convertCropPathToGeojson(croppath, sheetVersion)
  );

  const [mode, setMode] = useState<CustomDrawPolygonMode | EditingMode>(
    new CustomDrawPolygonMode()
  );

  const defaultFeatureStyles = {
    stroke: 'rgba(0, 116, 204)',
    fill: 'rgba(0, 116, 204, 0.2)',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    strokeDasharray: 3,
  };
  const defaultHandleStyles = {
    height: 15,
    strokeWidth: 0,
    width: 15,
    x: -7.5,
    y: -7.5,
    fillOpacity: 1,
  };

  const getFeatureStyles = ({ feature }: any) => {
    if (feature.geometry.type === 'Polygon') {
      return { ...defaultFeatureStyles, cursor: 'move' };
    }
    return defaultFeatureStyles;
  };

  const getEditHandleStyles = ({ index, state }: any) => {
    const isHovered = state === RENDER_STATE.HOVERED;
    const fill =
      isHovered || index === 0 || index === -1
        ? 'rgb(0, 134, 54)'
        : 'rgba(0, 116, 204)';

    return {
      ...defaultHandleStyles,
      fill,
    };
  };

  const getMapCursorStyles = ({ isDragging }: any) => {
    return isDragging ? 'grabbing' : 'copy';
  };

  const onFeatureUpdate = (newFeatures: ReactMapGLFeatures) => {
    const hasCompletedPolygon = newFeatures.data.length === 1;
    const { editType } = newFeatures;
    if (
      hasCompletedPolygon &&
      editType !== 'movePosition' &&
      editType !== 'addPosition'
    ) {
      setMode(new EditingMode());
    }

    setFeaturesData(newFeatures.data);
  };

  const closePolygon = () => {
    if (mode instanceof CustomDrawPolygonMode) {
      mode.closePolygon();
    }
  };

  const removePoint = () => {
    if (mode instanceof CustomDrawPolygonMode) {
      mode.removeLastPoint();
    }
  };

  useEffect(() => {
    if (enterPressed) {
      closePolygon();
    }
    if (backSpacePressed) {
      removePoint();
    }
  }, [enterPressed, backSpacePressed]);

  const zoomMap = (zoomIn: boolean) => {
    const zoomIncrement = zoomIn ? 1 : -1;

    let newZoom = viewport.zoom + zoomIncrement;

    if (newZoom <= minZoomLevel) {
      newZoom = minZoomLevel;
    }

    setViewport((prev) => ({
      ...prev,
      zoom: newZoom,
      transitionDuration: defaultFlyAnimationDuration,
      transitionInterpolator: new LinearInterpolator(),
    }));
  };

  return (
    <div id="crop-layer-container" className="column h100 w100">
      <div className="row mx3 my2 justify-between items-center">
        <div className="font-primary font-size-5 black-2 row items-center">
          <BackButton
            onClick={() => {
              calibrationMachineService.send({
                type: CalibrationMachineEventsEnum.BACK_TO_SCALE,
                data: {
                  croppath: polygonToCropPath(featuresData, sheetVersion),
                },
              });
            }}
          />
          <span className="medium mr1">
            {translate('mapView:sheetCropper.stepTwoOfThree')}{' '}
          </span>
          {translate('mapView:sheetCropper.cropDrawing')}
          <Tooltip
            title={translate('mapView:sheetCropper.croppingDetails')}
            content={
              <>
                <p className="font-size-4 normal mb3">
                  {translate('mapView:sheetCropper.croppingDetailsTextOne')}
                </p>
                <p className="font-size-4 normal m0">
                  {translate('mapView:sheetCropper.croppingDetailsTextTwo')}
                </p>
              </>
            }
          >
            <TooltipButton variant="info" />
          </Tooltip>
        </div>
        <div className="row">
          <Button
            id="map-prepare-crop-cancel-btn"
            variant="tertiary"
            className="mr3"
            onClick={actions.clearAllModals}
          >
            {translate('buttons:cancel')}
          </Button>
          <Button
            id="map-prepare-crop-next-btn"
            variant="primary"
            disabled={featuresData.length === 0}
            onClick={() => {
              calibrationMachineService.send({
                type: CalibrationMachineEventsEnum.SAVE_CROP,
                data: {
                  croppath: polygonToCropPath(featuresData, sheetVersion),
                },
              });
            }}
          >
            {translate('buttons:nextStep')}
          </Button>
        </div>
      </div>
      <div
        id="cropper-map-container"
        className="cropper-map-container h100 w100 pos-r"
        style={{
          height: '100%',
          width: '100%',
        }}
      >
        <ReactMapGL
          {...viewport}
          height="100%"
          mapboxApiAccessToken={environmentVars.MAPBOX_API_KEY}
          maxPitch={60}
          maxZoom={maxZoomLevel}
          minZoom={minZoomLevel}
          attributionControl={false}
          onViewportChange={setViewport}
          width="100%"
          getCursor={featuresData.length === 1 ? undefined : getMapCursorStyles}
        >
          <Layer type="background" paint={{ 'background-color': '#c3c5c9' }} />
          <SheetImageSource
            mapContainerId="cropper-map-container"
            sheetVersion={sheetVersion}
            onFitImageViewportCalculated={(fitImageViewport) =>
              setViewport(fitImageViewport)
            }
          />
          <ReactMapGLDraw
            clickRadius={12}
            // @ts-ignore-next-line
            features={featuresData}
            featureStyle={getFeatureStyles}
            editHandleShape="rect"
            editHandleStyle={getEditHandleStyles}
            mode={mode}
            onUpdate={onFeatureUpdate}
            selectable
          />
        </ReactMapGL>
        <ZoomInOut
          id="map-prepare-crop"
          disableZoomIn={viewport.zoom === maxZoomLevel}
          disableZoomOut={viewport.zoom <= minZoomLevel}
          handleZoomIn={() => zoomMap(true)}
          handleZoomOut={() => zoomMap(false)}
        />
      </div>
    </div>
  );
};

export default SheetCropper;
